import React from 'react';

import Badge from '../common/Badge';
import { Button } from '@copilot/ui';
import Icon from '../common/Icon';

// Mirrors desktop/src/services/backendDocuments.ts's documentStatus
// exactly - same labels, same detail copy, same conceptual tones - so
// the same document reads the same way on both clients. Only the badge
// variant names differ (web's existing badge color set: paused =
// amber, listening = green, disconnected = red, reconnecting = accent/
// neutral) since that mapping already exists in this app.
const STATUS_PRESENTATION = {
  completed: { variant: 'listening', label: 'Ready', detail: 'Available for meetings' },
  pending: { variant: 'paused', label: 'Queued', detail: 'Processing has not started yet' },
  processing: { variant: 'paused', label: 'Processing', detail: 'Available after processing finishes' },
  failed: { variant: 'disconnected', label: 'Failed', detail: 'Document processing failed' },
};

function documentStatus(document) {
  return (
    STATUS_PRESENTATION[document.processing_status] || {
      variant: 'reconnecting',
      label: 'Unavailable',
      detail: 'This document is not ready',
    }
  );
}

function documentTypeLabel(document) {
  return (
    document.file_type ||
    document.file_name.split('.').pop() ||
    'file'
  ).toUpperCase();
}

function documentSize(bytes) {
  if (typeof bytes !== 'number') return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function documentDate(isoString) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return 'Recently';

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * One row per document, ported from desktop/src/pages/DocumentsPage.tsx
 * (its inline row markup - .backend-document-row/-icon/-main/-title)
 * rather than this file's previous badge-heavy single-line layout, so
 * both clients show the exact same title/status/metadata/detail
 * structure for the same document.
 */
function DocumentList({ documents, onView, onDelete, deleteDisabled }) {
  return (
    <div className="backend-document-list">
      {documents.map((document) => {
        const status = documentStatus(document);

        return (
          <article className="backend-document-row" key={document.id}>
            <span className="backend-document-icon">
              <Icon name="file-text" size={18} />
            </span>

            <div className="backend-document-main">
              <div className="backend-document-title">
                <strong>{document.file_name}</strong>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>
              <span className="backend-document-meta">
                {documentTypeLabel(document)} -{' '}
                {document.type === 'manual' ? 'Manual' : documentSize(document.file_size)} - Added{' '}
                {documentDate(document.created_at)}
              </span>
              {document.processing_status !== 'completed' && (
                <span className="backend-document-meta">{status.detail}</span>
              )}
            </div>

            <div className="backend-document-actions">
              <Button
                variant="secondary"
                aria-label={`View ${document.file_name}`}
                onClick={() => onView(document)}
              >
                View
              </Button>
              <Button
                variant="danger"
                disabled={deleteDisabled}
                aria-label={`Delete ${document.file_name}`}
                onClick={() => onDelete(document)}
              >
                Delete
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default DocumentList;
